import { reactive } from "vue";

export interface CompanyTerminology {
  shortName: string;
  fullName: string;
}

export const DEFAULT_COMPANY_TERMINOLOGY: CompanyTerminology = {
  shortName: "SPMB",
  fullName: "Sistem Penerimaan Murid Baru",
};

interface CompanyTerminologyConfig {
  default?: Partial<CompanyTerminology>;
  companies?: Record<string, Partial<CompanyTerminology>>;
}

let terminologyConfig: CompanyTerminologyConfig = {};
let activeCompanyId: string | undefined;

export const companyTerminology = reactive<CompanyTerminology>({
  ...DEFAULT_COMPANY_TERMINOLOGY,
});

export function resolveCompanyTerminology(
  companyId?: string | null,
): CompanyTerminology {
  const companyOverride = companyId
    ? terminologyConfig.companies?.[companyId]
    : undefined;

  return {
    ...DEFAULT_COMPANY_TERMINOLOGY,
    ...validTerminology(terminologyConfig.default),
    ...validTerminology(companyOverride),
  };
}

function validTerminology(
  value?: Partial<CompanyTerminology>,
): Partial<CompanyTerminology> {
  if (!value || typeof value !== "object") {
    return {};
  }

  return {
    ...(typeof value.shortName === "string"
      ? { shortName: value.shortName }
      : {}),
    ...(typeof value.fullName === "string" ? { fullName: value.fullName } : {}),
  };
}

function parseTerminologyConfig(value: unknown): CompanyTerminologyConfig {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  const config = value as CompanyTerminologyConfig;
  const companies: Record<string, Partial<CompanyTerminology>> = {};

  if (
    config.companies &&
    typeof config.companies === "object" &&
    !Array.isArray(config.companies)
  ) {
    Object.keys(config.companies).forEach((companyId) => {
      companies[companyId] = validTerminology(config.companies![companyId]);
    });
  }

  return {
    default: validTerminology(config.default),
    companies,
  };
}

export async function loadCompanyTerminologyConfig(): Promise<CompanyTerminology> {
  try {
    const response = await fetch(
      `${process.env.BASE_URL}data/company-terminology.json`,
      { cache: "no-cache" },
    );

    if (!response.ok) {
      throw new Error("Company terminology config could not be loaded");
    }

    terminologyConfig = parseTerminologyConfig(await response.json());
  } catch {
    terminologyConfig = {};
  }

  return setCompanyTerminology(activeCompanyId);
}

export function setCompanyTerminology(
  companyId?: string | null,
): CompanyTerminology {
  activeCompanyId = companyId || undefined;
  const terminology = resolveCompanyTerminology(activeCompanyId);
  Object.assign(companyTerminology, terminology);

  if (typeof document !== "undefined") {
    document.title = `${terminology.shortName} | ${terminology.fullName}`;
  }

  return terminology;
}

export function syncCompanyTerminologyFromStorage(): CompanyTerminology {
  let companyId: string | undefined;

  try {
    const userAccount = JSON.parse(
      localStorage.getItem("user_account") || "{}",
    );
    companyId =
      typeof userAccount?.school_id === "string"
        ? userAccount.school_id
        : undefined;
  } catch {
    companyId = undefined;
  }

  return setCompanyTerminology(companyId);
}
